import React from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import type { Job } from "../../../../../../models";
import { getValidationErrorKey } from "../../../../../../utilities/jobValidation";
import { ColumnContext } from "../standard-column-types";
import { columnDisplayNames } from "../../../../../admin/ImportSettings";
import { useNextCropColumnViewModel } from "./NextCrop.column.vm";
import { NextCropColumnViewModel } from "./NextCrop.column.types";
import { useValidationErrors } from "../../shared/hooks/ValidationErrorsContext";
import { useIsRowInEditMode, useEditModeContext } from "../../shared/hooks/EditModeContext";

interface NextCropColumnCellProps {
	row: MRT_Row<Job>;
	vm: NextCropColumnViewModel;
	cropOptions: string[];
}

/**
 * Memoized Cell component for NextCrop column
 * Prevents unnecessary re-renders when column definitions change
 */
const NextCropColumnCell = React.memo(
	function NextCropColumnCell({ row, vm, cropOptions }: NextCropColumnCellProps) {
		const job = row.original;
		const validationErrors = useValidationErrors();
		const isInEditMode = useIsRowInEditMode(job.id);
		const { exitRowEdit } = useEditModeContext();
		const validationKey = getValidationErrorKey("nextCrop", job.id);
		const currentValue = vm.getCurrentValue(job);
		const showOtherMessage = currentValue === "Other";
		const isRequired = vm.getIsRequired(row);
		const hasError = !!validationErrors?.[validationKey];

		// View Mode: render crop name for performance
		if (!isInEditMode) {
			return (
				<Box
					title={hasError ? validationErrors[validationKey] : currentValue || undefined}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						color: hasError ? "error.main" : "inherit",
						borderBottom: hasError ? "2px solid" : "none",
						borderColor: "error.main",
						width: "100%",
						px: 0.5,
					}}
				>
					{currentValue || <span style={{ color: "#999" }}>-</span>}
				</Box>
			);
		}

		return (
			<Autocomplete
				size="small"
				style={{ width: "100%", padding: 0 }}
				value={currentValue}
				options={cropOptions}
				renderInput={(params) => (
					<TextField
						style={{ padding: 0 }}
						{...params}
						required={isRequired}
						error={hasError}
						helperText={
							showOtherMessage ? "Please put the crop type in Submission Notes" : validationErrors?.[validationKey]
						}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								exitRowEdit(job.id);
							}
						}}
					/>
				)}
				onChange={(_event, newValue) => {
					vm.handleChange(job.id, newValue);
				}}
				getOptionLabel={(option) => option || ""}
			/>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison for better memoization
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob.nextCrop === nextJob.nextCrop &&
			prevProps.cropOptions === nextProps.cropOptions
		);
	},
);

interface NextCropColumnAggregatedCellProps {
	row: MRT_Row<Job>;
	vm: NextCropColumnViewModel;
	cropOptions: string[];
}

/**
 * Memoized AggregatedCell component for NextCrop column
 * Renders editable Autocomplete when group row is in edit mode
 */
const NextCropColumnAggregatedCell = React.memo(
	function NextCropColumnAggregatedCell({ row, vm, cropOptions }: NextCropColumnAggregatedCellProps) {
		// For group rows, check edit mode using row.id (e.g., "grower:Alexander, Curt")
		const isInEditMode = useIsRowInEditMode(row.id);
		const { exitRowEdit } = useEditModeContext();

		// Get value from first job in group
		const job = row.original;
		const currentValue = vm.getCurrentValue(job);

		// View Mode: render crop name
		if (!isInEditMode) {
			return (
				<Box
					title={currentValue || undefined}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						width: "100%",
						px: 0.5,
					}}
				>
					{currentValue || <span style={{ color: "#999" }}>-</span>}
				</Box>
			);
		}

		// Edit Mode: render Autocomplete that updates all rows in group
		return (
			<Autocomplete
				size="small"
				style={{ width: "100%", padding: 0 }}
				value={currentValue}
				options={cropOptions}
				renderInput={(params) => (
					<TextField
						style={{ padding: 0 }}
						{...params}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								exitRowEdit(row.id);
							}
						}}
					/>
				)}
				onChange={(_event, newValue) => {
					vm.handleGroupChange(row, newValue);
				}}
				getOptionLabel={(option) => option || ""}
			/>
		);
	},
	(prevProps, nextProps) => {
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob.nextCrop === nextJob.nextCrop &&
			prevProps.cropOptions === nextProps.cropOptions
		);
	},
);

/**
 * Build the NextCrop column
 * Uses memoized Cell components to prevent unnecessary re-renders when column definitions change
 */
export function column_NextCrop(context: ColumnContext): MRT_ColumnDef<Job, unknown> {
	const vm = useNextCropColumnViewModel(context);
	const cropOptions = context.props.nextCropOptions ?? [];

	return {
		id: "nextCrop",
		header: columnDisplayNames.nextCrop,
		accessorKey: "nextCrop",
		enableEditing: false,
		minSize: 150,
		Cell: ({ row }: { row: MRT_Row<Job> }) => <NextCropColumnCell row={row} vm={vm} cropOptions={cropOptions} />,
		AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => (
			<NextCropColumnAggregatedCell row={row} vm={vm} cropOptions={cropOptions} />
		),
	};
}
